import { useCallback, useEffect, useRef, useState } from 'react';
import type { Operaciones } from '../api/operaciones';
import { ErrorApi } from '../api/problema';
import { aplicar, descifrar, loteDe, tras, type Copia, type Pendiente, type ResumenEnvio } from './copiaPuerta';

/**
 * La copia de la puerta en la tablet (6.2, G2): se baja con la sesión con PIN (`createDoorPackage`, D-067, con la parte del
 * cupo de taquilla que pide la tablet), se actualiza cada minuto con la credencial de la tablet (`getDoorPackageContent`,
 * entera la primera vez y luego los cambios), y lo leído sin conexión se envía en un lote al volver (`syncDoorPackage`).
 * Si el servidor manda borrarla (`wipe_required`), se borra y se confirma (`confirmDoorPackageWipe`).
 *
 * En el dispositivo (G820, ADR-0209) se guarda en `localStorage` cifrada: la copia, con la clave del paquete (AES-256-GCM);
 * y todo lo guardado (la clave del paquete, lo leído y lo vendido en taquilla sin conexión aún sin enviar…), con una **clave por dispositivo**
 * (AES-256-GCM, aleatoria, creada la primera vez) que va aparte. Nada queda en claro: ni un código ni un nombre. Al
 * borrar el paquete se borran la copia y su clave. El navegador no tiene almacén seguro sin el PIN de tablet: la clave
 * protege contra copias del almacén, no contra quien tiene la tablet desbloqueada. «Última conexión» es la hora del
 * servidor de su última respuesta (`server_time`, G423).
 */
const CLAVE = 'reservaria.tablet.puerta';
/** La clave por dispositivo (G820): base64 de 32 bytes aleatorios. */
const CLAVE_DISPOSITIVO = 'reservaria.tablet.puerta.clave';
export const CADA_MINUTO = 60_000;

/**
 * Las escrituras de todas las copias abiertas van en una sola fila (la Puerta y la Taquilla de la tablet usan cada una su
 * `useCopia`): al pasar de una a otra, la que se abre lee después de la última escritura de la que se cierra.
 */
let filaDelDispositivo: Promise<void> = Promise.resolve();

interface Guardado {
    negocio: string;
    paquete: string;
    grupo: string;
    clave: string;
    version: number | null;
    cifrado: string | null;
    nonce: string | null;
    servidor: string | null;
    /** País del negocio de la última copia (`business_country`, G986), para el prefijo del móvil sin conexión. */
    pais?: string | null;
    pendientes: Pendiente[];
}

type Almacen = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function almacenLocal(): Almacen | null {
    try {
        return typeof window === 'undefined' ? null : window.localStorage;
    } catch {
        return null;
    }
}

const aBase64 = (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b instanceof Uint8Array ? b : new Uint8Array(b))));
const deBase64 = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

/** La clave por dispositivo; con `crear`, la hace si no hay (la primera vez que se guarda algo). */
async function claveDelDispositivo(a: Almacen | null, crear: boolean): Promise<CryptoKey | null> {
    let b64: string | null = null;
    try {
        b64 = a?.getItem(CLAVE_DISPOSITIVO) ?? null;
        if (b64 === null && crear) {
            b64 = aBase64(crypto.getRandomValues(new Uint8Array(32)));
            a?.setItem(CLAVE_DISPOSITIVO, b64);
        }
    } catch {
        // Sin almacenamiento: no hay clave guardada.
    }
    return b64 === null ? null : crypto.subtle.importKey('raw', deBase64(b64), { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

const esGuardado = (v: unknown): v is Guardado => typeof v === 'object' && v !== null && typeof (v as Guardado).paquete === 'string' && typeof (v as Guardado).clave === 'string';

async function leer(a: Almacen | null): Promise<Guardado | null> {
    try {
        const v = JSON.parse(a?.getItem(CLAVE) ?? 'null') as { nonce?: unknown; datos?: unknown } | null;
        if (v === null) {
            return null;
        }
        let g: unknown = v;
        if (typeof v.nonce === 'string' && typeof v.datos === 'string') {
            const clave = await claveDelDispositivo(a, false);
            if (clave === null) {
                return null;
            }
            const claro = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: deBase64(v.nonce) }, clave, deBase64(v.datos));
            g = JSON.parse(new TextDecoder().decode(claro));
        }
        // Lo de antes de G820 (en claro) se lee una vez y se vuelve a guardar cifrado.
        return esGuardado(g) ? { ...g, pendientes: Array.isArray(g.pendientes) ? g.pendientes : [] } : null;
    } catch {
        return null;
    }
}

async function escribir(a: Almacen | null, g: Guardado | null): Promise<void> {
    try {
        if (g === null) {
            // Se borran la copia y su clave (G820).
            a?.removeItem(CLAVE);
            a?.removeItem(CLAVE_DISPOSITIVO);
            return;
        }
        const clave = await claveDelDispositivo(a, true);
        if (clave === null) {
            return;
        }
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const datos = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, clave, new TextEncoder().encode(JSON.stringify(g)));
        a?.setItem(CLAVE, JSON.stringify({ nonce: aBase64(iv), datos: aBase64(datos) }));
    } catch {
        // Sin almacenamiento: vive solo en memoria.
    }
}

async function cifrar(copia: Copia, claveB64: string): Promise<{ cifrado: string; nonce: string }> {
    const crudo = Uint8Array.from(atob(claveB64), (c) => c.charCodeAt(0));
    const clave = await crypto.subtle.importKey('raw', crudo, { name: 'AES-GCM' }, false, ['encrypt']);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const cifrado = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, clave, new TextEncoder().encode(JSON.stringify(copia)));
    return { cifrado: aBase64(cifrado), nonce: aBase64(iv) };
}

export interface EstadoCopia {
    /** La copia descifrada (solo en memoria); null sin copia. */
    copia: Copia | null;
    hayCopia: boolean;
    grupo: string | null;
    /** Última respuesta del servidor (`server_time`). */
    ultimaRespuesta: Date | null;
    /** País del negocio que trae la copia (`business_country`, G986); null sin copia leída. */
    pais: string | null;
    pendientes: Pendiente[];
    ocupada: boolean;
    enviando: boolean;
    resultado: ResumenEnvio | null;
    error: string | null;
}

export function useCopia({ ops, opsTablet, negocio, conConexion, almacen: dado }: { ops: Operaciones; opsTablet: Operaciones; negocio: string; conConexion: boolean; almacen?: Almacen | null }) {
    const [almacen] = useState<Almacen | null>(() => (dado === undefined ? almacenLocal() : dado));
    // Se lee al abrir (descifrar es asíncrono); hasta entonces, sin copia.
    const [guardado, setGuardado] = useState<Guardado | null>(null);
    const [copia, setCopia] = useState<Copia | null>(null);
    const [ocupada, setOcupada] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [resultado, setResultado] = useState<ResumenEnvio | null>(null);
    const [error, setError] = useState<string | null>(null);
    const actual = useRef(guardado);
    useEffect(() => {
        actual.current = guardado;
    }, [guardado]);

    // Las escrituras van en fila: cada una cifra lo último y nunca adelanta a la anterior.
    const guardar = useCallback((g: Guardado | null) => {
        actual.current = g;
        filaDelDispositivo = filaDelDispositivo.then(() => escribir(almacen, g));
        setGuardado(g);
    }, [almacen]);

    // Al abrir, lo guardado se descifra con la clave del dispositivo (si es de este negocio).
    useEffect(() => {
        let vivo = true;
        void filaDelDispositivo.then(() => leer(almacen)).then((g) => {
            if (vivo && g !== null && g.negocio === negocio && actual.current === null) {
                guardar(g);
            }
        });
        return () => {
            vivo = false;
        };
    }, [almacen, negocio, guardar]);

    // La copia guardada se descifra en memoria.
    const cifradoGuardado = guardado?.cifrado ?? null;
    useEffect(() => {
        const g = actual.current;
        if (g?.cifrado == null || g.nonce == null || copia !== null) {
            return;
        }
        let vivo = true;
        descifrar({ ciphertext: g.cifrado, nonce: g.nonce }, g.clave)
            .then((c) => {
                if (vivo) {
                    setCopia(c);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [copia, cifradoGuardado]);

    const borrarLocal = useCallback(
        (paquete: string) => {
            guardar(null);
            setCopia(null);
            opsTablet.confirmDoorPackageWipe(negocio, paquete).catch(() => undefined);
        },
        [guardar, negocio, opsTablet],
    );

    const refrescar = useCallback(async () => {
        const g = actual.current;
        if (g === null) {
            return;
        }
        try {
            const r = await opsTablet.getDoorPackageContent(negocio, g.paquete, g.version);
            const nueva = await descifrar(r, g.clave);
            const base = g.cifrado === null || g.nonce === null ? null : await descifrar({ ciphertext: g.cifrado, nonce: g.nonce }, g.clave).catch(() => null);
            const junta = aplicar(base, nueva, r.kind);
            const { cifrado, nonce } = await cifrar(junta, g.clave);
            const siguiente = { ...(actual.current ?? g), version: r.content_version, cifrado, nonce, servidor: r.server_time ?? new Date().toISOString(), pais: r.business_country };
            guardar(siguiente);
            setCopia(junta);
            setError(null);
        } catch (e) {
            if (e instanceof ErrorApi && e.estado === 409 && (e.problema as Record<string, unknown>).wipe_required === true) {
                borrarLocal(g.paquete);
            }
        }
    }, [borrarLocal, guardar, negocio, opsTablet]);

    /** «Descargar la copia»: con la sesión con PIN y la parte del cupo de taquilla que pide esta tablet. */
    const descargar = useCallback(
        async (grupo: string, cupo: number) => {
            setOcupada(true);
            setError(null);
            try {
                const p = await ops.createDoorPackage(negocio, grupo, cupo);
                guardar({ negocio, paquete: p.id, grupo, clave: p.data_key, version: null, cifrado: null, nonce: null, servidor: null, pendientes: actual.current?.pendientes ?? [] });
                await refrescar();
            } catch (e) {
                setError(e instanceof ErrorApi ? (e.problema.detail ?? e.problema.title) : 'No he podido descargar la copia. Prueba de nuevo.');
            } finally {
                setOcupada(false);
            }
        },
        [guardar, negocio, ops, refrescar],
    );

    // Con conexión, la copia se actualiza cada minuto.
    const paquete = guardado?.paquete ?? null;
    useEffect(() => {
        if (!conConexion || paquete === null) {
            return;
        }
        const reloj = setInterval(() => {
            void refrescar();
        }, CADA_MINUTO);
        return () => {
            clearInterval(reloj);
        };
    }, [conConexion, paquete, refrescar]);

    /** Lo leído (o, en la taquilla, la venta y sus llegadas) sin conexión, para enviarlo al volver. */
    const anotar = useCallback(
        (p: Pendiente | readonly Pendiente[]) => {
            const g = actual.current;
            const nuevos: readonly Pendiente[] = 'item' in p ? [p] : p;
            if (g !== null) {
                guardar({ ...g, pendientes: [...g.pendientes, ...nuevos] });
            }
        },
        [guardar],
    );

    /** Al volver la conexión: lo leído sin conexión, en un lote. */
    const enviar = useCallback(async () => {
        const g = actual.current;
        if (g === null || g.pendientes.length === 0) {
            return;
        }
        setEnviando(true);
        try {
            const r = await opsTablet.syncDoorPackage(negocio, g.paquete, loteDe(g.pendientes, new Date()));
            const res = tras(g.pendientes, r);
            setResultado(res);
            if (res.borrar && res.quedan.length === 0) {
                borrarLocal(g.paquete);
            } else {
                guardar({ ...(actual.current ?? g), pendientes: res.quedan });
                void refrescar();
            }
        } catch {
            // Se vuelve a intentar con la próxima conexión.
        } finally {
            setEnviando(false);
        }
    }, [borrarLocal, guardar, negocio, opsTablet, refrescar]);

    const pendientes = guardado?.pendientes ?? [];
    const hayPendientes = pendientes.length > 0;
    useEffect(() => {
        if (conConexion && hayPendientes) {
            void enviar();
        }
    }, [conConexion, hayPendientes, enviar]);

    const estado: EstadoCopia = {
        copia,
        hayCopia: guardado !== null,
        grupo: guardado?.grupo ?? null,
        ultimaRespuesta: guardado?.servidor == null ? null : new Date(guardado.servidor),
        pais: guardado?.pais ?? null,
        pendientes,
        ocupada,
        enviando,
        resultado,
        error,
    };
    return { estado, descargar, refrescar, anotar, enviar, olvidarResultado: () => { setResultado(null); } };
}
